import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  BatchVerifyResponse,
  RenderMode,
  VerifyBatchBody,
  VerifyByDealBody,
  VerifyDocumentResponse,
  VerifyFromBlobBody,
  VerifyJobAccepted,
  VerifyJobStatus,
} from '../types';

export const documentVerificationApi = createApi({
  reducerPath: 'documentVerificationApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    // 1) Upload-and-verify a browser file (sync). `mode` is a query param.
    verifyUpload: builder.mutation<
      VerifyDocumentResponse,
      { file: File; mode?: RenderMode }
    >({
      query: ({ file, mode }) => {
        const form = new FormData();
        form.append('file', file);
        return {
          url: '/v1/documents/verify',
          method: 'POST',
          params: mode ? { mode } : undefined,
          body: form,
        };
      },
      transformResponse: (r: ApiResponse<VerifyDocumentResponse>) => r.data,
    }),

    // 2) Verify a document already in blob storage (sync).
    verifyFromBlob: builder.mutation<
      VerifyDocumentResponse,
      VerifyFromBlobBody
    >({
      query: (body) => ({
        url: '/v1/documents/verify/from-blob',
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<VerifyDocumentResponse>) => r.data,
    }),

    // 3) Batch verify (async → returns jobId).
    verifyBatch: builder.mutation<VerifyJobAccepted, VerifyBatchBody>({
      query: (body) => ({
        url: '/v1/documents/verify/batch',
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<VerifyJobAccepted>) => r.data,
    }),

    // 4) Verify a deal's categorized files (async → returns jobId).
    verifyByDeal: builder.mutation<VerifyJobAccepted, VerifyByDealBody>({
      query: (body) => ({
        url: '/v1/documents/verify/by-deal',
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<VerifyJobAccepted>) => r.data,
    }),

    // 5) Poll a job. Pass `pollingInterval` on the hook to keep it fresh.
    getVerifyJob: builder.query<VerifyJobStatus, string>({
      query: (jobId) => ({
        url: `/v1/documents/verify/jobs/${jobId}`,
        method: 'GET',
      }),
      transformResponse: (r: ApiResponse<VerifyJobStatus>) => r.data,
    }),
  }),
});

export const {
  useVerifyUploadMutation,
  useVerifyFromBlobMutation,
  useVerifyBatchMutation,
  useVerifyByDealMutation,
  useGetVerifyJobQuery,
} = documentVerificationApi;

export type { BatchVerifyResponse };
